'use client';

import React from 'react';
import { Form } from 'antd';

interface IntroductionProps {
  inlineError?: string | null;
}

const cardBase =
  'w-full rounded-xl border px-4 py-4 transition-all duration-150 select-none';
const rowBase = 'flex items-start gap-3';
const dotOuter =
  'mt-[2px] h-5 w-5 rounded-full border flex items-center justify-center transition-all';
const dotInner = 'h-[10px] w-[10px] rounded-full transition-all';

function RadioDot({
  checked,
  variant = 'green',
}: {
  checked: boolean;
  variant?: 'green' | 'yellow' | 'gray';
}) {
  const border =
    variant === 'green'
      ? checked
        ? 'border-[#16A34A]'
        : 'border-[#9CA3AF]'
      : variant === 'yellow'
      ? checked
        ? 'border-[#F59E0B]'
        : 'border-[#9CA3AF]'
      : checked
      ? 'border-[#6B7280]'
      : 'border-[#9CA3AF]';

  const fill =
    variant === 'green'
      ? 'bg-[#16A34A]'
      : variant === 'yellow'
      ? 'bg-[#F59E0B]'
      : 'bg-[#6B7280]';

  return (
    <span className={`${dotOuter} ${border}`}>
      <span className={`${dotInner} ${checked ? fill : 'bg-transparent'}`} />
    </span>
  );
}

function SelectCard({
  checked,
  onClick,
  title,
  description,
  variant = 'green',
}: {
  checked: boolean;
  onClick: () => void;
  title: string;
  description?: string;
  variant?: 'green' | 'yellow';
}) {
  const border = checked
    ? variant === 'green'
      ? 'border-[#16A34A]'
      : 'border-[#F59E0B]'
    : 'border-[#D1D5DB]';

  const bg = checked
    ? variant === 'green'
      ? 'bg-[#ECFDF5]'
      : 'bg-[#FFFBEB]'
    : 'bg-white';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
      className={`${cardBase} ${border} ${bg} cursor-pointer hover:bg-[#F9FAFB]`}
    >
      <div className={rowBase}>
        <RadioDot
          checked={checked}
          variant={
            checked ? (variant === 'yellow' ? 'yellow' : 'green') : 'gray'
          }
        />

        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold text-[#111827]">
            {title}
          </div>

          {description ? (
            <div className="mt-1 text-[13px] leading-relaxed text-[#4B5563]">
              {description}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const Introduction: React.FC<IntroductionProps> = () => {
  const form = Form.useFormInstance();
  const accountType = Form.useWatch('account_type', form);
  const individualSubtype = Form.useWatch('individual_subtype', form);

  const handleCardClick = (type: 'individual' | 'organization') => {
    form.setFieldsValue({ account_type: type });

    if (type !== 'individual') {
      form.setFieldsValue({ individual_subtype: undefined });
    }

    form.validateFields(['account_type']);
  };

  const handleSubtypeClick = (value: string) => {
    form.setFieldsValue({ individual_subtype: value });
    form.validateFields(['individual_subtype']);
  };

  const individualOptions = [
    { value: 'citizen', label: 'Citizen →' },
    { value: 'resident', label: 'Foreign national (Resident)' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <>
      <Form.Item
        name="account_type"
        validateTrigger="onChange"
        rules={[
          { required: true, message: 'Please select an account type' },
          {
            validator: (_, value) => {
              if (value === 'individual') return Promise.resolve();
              return Promise.reject(
                new Error('Selected option(s) currently not supported.'),
              );
            },
          },
        ]}
      >
        <div className="flex flex-col gap-4">
          <SelectCard
            checked={accountType === 'individual'}
            onClick={() => handleCardClick('individual')}
            title="Individual"
            description="Individuals must provide a National Identification (ID) Number"
            variant="green"
          />
        </div>
      </Form.Item>

      {accountType === 'individual' && (
        <div className="pt-1">
          <div className="flex gap-4">
            <div className="w-[3px] rounded-full bg-[#16A34A]" />

            <div className="flex-1 space-y-3">
              <div className="text-[14px] font-semibold text-[#111827]">
                Are you a Citizen or Non-Citizen?
              </div>

              <Form.Item
                name="individual_subtype"
                className="mb-0"
                validateTrigger="onChange"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === 'citizen') return Promise.resolve();

                      return Promise.reject(
                        new Error(
                          'Selected option(s) currently not supported.',
                        ),
                      );
                    },
                  },
                ]}
              >
                <div className="space-y-3">
                  {individualOptions.map((opt) => (
                    <SelectCard
                      key={opt.value}
                      checked={individualSubtype === opt.value}
                      onClick={() => handleSubtypeClick(opt.value)}
                      title={opt.label}
                      variant="yellow"
                    />
                  ))}
                </div>
              </Form.Item>
            </div>
          </div>
        </div>
      )}
    </>
  );
};;

export default Introduction;
